import { Component, Input, computed } from '@angular/core';

type DocKind =
  | 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx'
  | 'txt' | 'csv' | 'json' | 'xml' | 'zip' | 'rar' | '7z'
  | 'sql' | 'log' | 'md' | 'rtf' | 'ods';

@Component({
    selector: 'app-doctype',
    templateUrl: './doctype.component.html',
    styleUrls: ['./doctype.component.scss'],
    standalone: false
})
export class DoctypeComponent {
  /** File name or extension. Example: "report.pdf" or "pdf" */
  @Input() filenameOrExt = '';
  /** Icon width in px (height auto = 1.25 * size) */
  @Input() size = 64;

  private palette: Record<DocKind, { label: string; color: string }> = {
    pdf: { label: 'PDF', color: '#e53935' },
    doc: { label: 'DOC', color: '#1e66f5' },
    docx: { label: 'DOCX', color: '#1e66f5' },
    ods: { label: 'ODS', color: '#2e7d32' },
    xls: { label: 'XLS', color: '#2e7d32' },
    xlsx: { label: 'XLSX', color: '#2e7d32' },
    ppt: { label: 'PPT', color: '#e65100' },
    pptx: { label: 'PPTX', color: '#e65100' },
    txt: { label: 'TXT', color: '#616161' },
    csv: { label: 'CSV', color: '#00897b' },
    json: { label: 'JSON', color: '#7b1fa2' },
    xml: { label: 'XML', color: '#6a1b9a' },
    zip: { label: 'ZIP', color: '#f9a825' },
    rar: { label: 'RAR', color: '#f9a825' },
    '7z': { label: '7Z', color: '#f9a825' },
    sql: { label: 'SQL', color: '#3949ab' },
    log: { label: 'LOG', color: '#455a64' },
    md: { label: 'MD', color: '#546e7a' },
    rtf: { label: 'RTF', color: '#5e35b1' },
  };

  private static imageExts = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif']);

  private ext = computed(() => {
    const s = (this.filenameOrExt || '').trim().toLowerCase();
    if (!s) return '';
    const e = s.includes('.') ? s.split('.').pop()! : s;
    return e.replace(/[^a-z0-9]/g, '');
  });

  accent = computed(() => {
    const hit = (this.palette as any)[this.ext()];
    return hit ? hit.color : '#9e9e9e';
  });

  label = computed(() => {
    const e = this.ext();
    const hit = (this.palette as any)[e];
    if (hit) return hit.label;
    if (!e) return 'FILE';
    if (DoctypeComponent.isImage(e)) return e.toUpperCase(); // you can special-case if needed
    return e.toUpperCase();
  });

  static isImage(ext: string) {
    return this.imageExts.has(ext);
  }

  badgeFill() {
    const c = this.accent();
    return this.darken(c, 0.12);
  }

  fontSize() {
    const len = this.label().length;
    return len <= 4 ? 18 : len === 5 ? 16 : 14;
  }

  ariaLabel() {
    return `Document icon ${this.label()}`;
  }

  private darken(hex: string, amt = 0.15) {
    const [r, g, b] = this.hexToRgb(hex);
    const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * (1 - amt))));
    return `rgb(${to(r)}, ${to(g)}, ${to(b)})`;
  }

  private hexToRgb(hex: string): [number, number, number] {
    const m = hex.trim().toLowerCase();
    const h = m.startsWith('#') ? m.slice(1) : m;
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(n, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  }
}


